// ============================================================================
// components/remote/useBrowserContractHost.ts — браузерный хост «Удалённого
// доступа» ЧЕРЕЗ РЕЛЕЙ сервера (наш wire-контракт v2, роль host). Хук связывает
// BrowserContractHost (захват+кодер+WS) с платформой: выдача токена релея
// (IssueRelayToken), регистрация адреса (transport='relay'), сигнал
// обнаружения сеанса по шине. Фазы — явные (ждём вторую сторону / соединено).
// ============================================================================

import { useCallback, useRef, useState } from 'react';
import { logEvent } from '@/lib/eventLog';
import { issueRelayToken, registerRemoteEndpointOnPlatform, relaySocketUrl } from '@/lib/remote/contractEndpoint';
import { publishRemoteSignal } from '@/lib/remote/remoteSession';
import { BrowserContractHost, isBrowserContractHostSupported, type BrowserHostPhase, type BrowserHostStats } from '@/lib/remote/contractHost';

export function useBrowserContractHost() {
  const supported = isBrowserContractHostSupported();
  const [phase, setPhase] = useState<BrowserHostPhase | ''>('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [stats, setStats] = useState<BrowserHostStats>({ fps: 0, kbps: 0 });
  const [busy, setBusy] = useState(false);
  const hostRef = useRef<BrowserContractHost | null>(null);

  const log = useCallback((level: 'info' | 'error', message: string, details?: string) => {
    void logEvent({ level, category: 'ui', message, entityType: 'remote', entityCode: 'host-relay', details: details || '' });
  }, []);

  const start = useCallback(async (sessionId: string, quality: string) => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      if (!supported) {
        throw new Error('Показ экрана из браузера недоступен: нет захвата экрана или кодека H.264 (WebCodecs)');
      }
      const sid = sessionId.trim();
      if (!sid) throw new Error('Укажите идентификатор сеанса');
      // 1) Токен релея (роль host) — пара «session_id + роль», TTL ~10 мин.
      const { token, relayUrl } = await issueRelayToken(sid, 'host');
      setEndpoint(relayUrl);
      // 2) Хост: захват → кодер → исходящее соединение с релеем.
      const host = new BrowserContractHost({
        url: relaySocketUrl(relayUrl, 'host', token),
        sessionId: sid,
        quality,
        onPhase: (p, message) => {
          setPhase(p);
          if (message) setNotice(message);
        },
        onError: (m) => setError(m),
        onNotice: (m) => setNotice(m),
        onStats: (s) => setStats(s),
      });
      hostRef.current = host;
      try {
        await host.start();
      } catch (e) {
        hostRef.current = null;
        throw e;
      }
      // 3) Адрес сеанса — на платформе (зритель резолвит по sessionId, БЕЗ токена) + сигнал.
      await registerRemoteEndpointOnPlatform(sid, relayUrl, 'relay');
      publishRemoteSignal(`remote.session.endpoint.${sid}`, { url: relayUrl, transport: 'relay' });
      log('info', 'удалённый доступ (релей): показ начат', relayUrl);
    } catch (e) {
      const msg = String((e as Error)?.message || e);
      setError(msg);
      setPhase('error');
      log('error', 'удалённый доступ (релей): не удалось начать показ', msg);
    } finally {
      setBusy(false);
    }
  }, [supported, log]);

  const stop = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const host = hostRef.current;
      hostRef.current = null;
      host?.stop();
      setPhase('stopped');
      setStats({ fps: 0, kbps: 0 });
      setEndpoint('');
      log('info', 'удалённый доступ (релей): показ остановлен');
    } finally {
      setBusy(false);
    }
  }, [log]);

  return { supported, phase, error, notice, endpoint, stats, busy, start, stop };
}

export type BrowserContractHostApi = ReturnType<typeof useBrowserContractHost>;
